import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FaWhatsapp } from "react-icons/fa";
import { X } from "lucide-react";
import { waLink } from "@/lib/brand";
import { useT } from "@/lib/i18n";

export default function FloatingWhatsApp() {
  const { t } = useT();
  const [showLabel, setShowLabel] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("unity_wa_dismissed")) return;
    const t1 = setTimeout(() => setShowLabel(true), 6000);
    const t2 = setTimeout(() => setShowLabel(false), 14000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  const dismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowLabel(false);
    setDismissed(true);
    sessionStorage.setItem("unity_wa_dismissed", "1");
  };

  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > window.innerHeight * 0.6);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 transition-[opacity,transform] duration-300 ${scrolled ? "" : "max-md:opacity-0 max-md:translate-y-4 max-md:pointer-events-none"}`}>
      <AnimatePresence>
        {showLabel && !dismissed && (
          <motion.div
            initial={{ opacity: 0, x: 20, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 20, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 220, damping: 22 }}
            className="hidden sm:flex items-center gap-3 bg-navy-800/95 backdrop-blur-xl border border-gold/30 text-white px-5 py-3 rounded-full shadow-2xl"
            data-testid="floating-whatsapp-label"
          >
            <span className="text-sm">
              {t("fwa.tip.q")}{" "}
              <span className="text-gold font-medium">{t("fwa.tip.a")}</span>
            </span>
            <button onClick={dismiss} className="h-6 w-6 inline-flex items-center justify-center rounded-full text-slate-400 hover:text-white hover:bg-white/5" aria-label="Dismiss">
              <X className="h-3 w-3" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <a
        href={waLink(t("wa.default"))}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="floating-whatsapp"
        aria-label={t("fwa.aria")}
        className="relative h-14 w-14 rounded-full bg-gold hover:bg-gold-light text-navy-900 inline-flex items-center justify-center shadow-[0_10px_30px_rgba(201,162,75,0.5)] transition-[background-color,transform] hover:scale-110"
      >
        <span aria-hidden className="absolute inset-0 rounded-full bg-gold/60 animate-pulse-ring pointer-events-none" />
        <FaWhatsapp className="relative h-6 w-6" />
      </a>
    </div>
  );
}
